import { useEffect, useRef } from "react";
import { UseQueryResult } from "@tanstack/react-query";

interface UseSubscriptionWithQueryOptions<T> {
  subscribe: () => Promise<{ unsubscribe: () => void }>;
  query: UseQueryResult<T>;
  enabled?: boolean;
  onError?: (error: unknown) => void;
}

/**
 * Generic hook that manages subscription lifecycle with React Query integration.
 * Handles race conditions and cleanup automatically.
 *
 * @template T - The type of data returned by the query
 * @param options - Configuration options
 * @param options.subscribe - Function that returns a subscription promise
 * @param options.query - React Query result hook
 * @param options.enabled - Whether the subscription should be active
 * @param options.onError - Optional error handler
 * @returns The query result for convenience
 */
export function useSubscriptionWithQuery<T>({
  subscribe,
  query,
  enabled = true,
  onError,
}: UseSubscriptionWithQueryOptions<T>): UseQueryResult<T> {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const cancelledRef = useRef(false);
  const subscriptionReadyRef = useRef(false);

  // Safe unsubscribe wrapper that handles errors gracefully
  const safeUnsubscribe = (unsubscribeFn: () => void) => {
    try {
      unsubscribeFn();
    } catch (error) {
      // Ignore WebSocket state errors (e.g., CONNECTING state)
      // These are expected when component unmounts before subscription is ready
      if (
        error instanceof Error &&
        error.name === "InvalidStateError" &&
        error.message.includes("CONNECTING state")
      ) {
        // Silently ignore - WebSocket wasn't ready yet
        return;
      }
      // Log other unexpected errors
      console.warn("Error during unsubscribe:", error);
    }
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    cancelledRef.current = false;
    subscriptionReadyRef.current = false;

    subscribe()
      .then((subscription) => {
        // Check if component was unmounted or effect was cancelled
        if (cancelledRef.current) {
          // Immediately unsubscribe if we're already cancelled
          safeUnsubscribe(subscription.unsubscribe);
          return;
        }
        subscriptionReadyRef.current = true;
        unsubscribeRef.current = subscription.unsubscribe;
      })
      .catch((error) => {
        if (!cancelledRef.current && onError) {
          onError(error);
        }
      });

    return () => {
      cancelledRef.current = true;
      if (unsubscribeRef.current) {
        safeUnsubscribe(unsubscribeRef.current);
        unsubscribeRef.current = null;
      }
    };
  }, [subscribe, enabled, onError]);

  return query;
}
